
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { fetchMentorProfile, fetchSessionAttendees, fetchAttendeeProfiles } from './officeHoursHelpers';

/**
 * Fetch all office hours sessions
 */
export const fetchAllOfficeHours = async () => {
  try {
    // First, let's get all office hours sessions
    const { data: officeHoursData, error: officeHoursError } = await supabase
      .from('office_hours')
      .select('*')
      .order('start_time', { ascending: true });
    
    if (officeHoursError) throw officeHoursError;
    
    // Now for each session, fetch the mentor data separately
    const sessionsWithMentors = await Promise.all(
      officeHoursData.map(async (session) => {
        // Get mentor profile data
        const mentor = await fetchMentorProfile(session.mentor_id);
        
        // Get attendees 
        const attendees = await fetchSessionAttendees(session.id);
        
        return {
          ...session,
          mentor,
          attendees
        };
      })
    );
    
    return sessionsWithMentors;
  } catch (error) {
    console.error('Error fetching office hours:', error);
    toast.error('Failed to fetch office hours');
    return [];
  }
};

/**
 * Fetch office hours for a specific mentor
 */
export const fetchMentorOfficeHours = async (mentorId: string) => {
  try {
    // Get the office hours for this mentor
    const { data: officeHoursData, error: officeHoursError } = await supabase
      .from('office_hours')
      .select('*')
      .eq('mentor_id', mentorId)
      .order('start_time', { ascending: true });
    
    if (officeHoursError) throw officeHoursError;
    
    // Now for each session, fetch the attendees data separately
    const sessionsWithAttendees = await Promise.all(
      officeHoursData.map(async (session) => {
        // Get attendees data
        const attendeesData = await fetchSessionAttendees(session.id);
        
        // For each attendee, fetch their profile data
        const attendeesWithProfiles = await fetchAttendeeProfiles(attendeesData);
        
        return {
          ...session,
          attendees: attendeesWithProfiles
        };
      })
    );
    
    return sessionsWithAttendees;
  } catch (error) {
    console.error('Error fetching mentor office hours:', error);
    toast.error('Failed to fetch mentor office hours');
    return [];
  }
};
